/**
 * Ввод кода из шести цифр — шесть отдельных ячеек.
 *
 * Одно длинное поле с разрядкой выглядело сломанным: курсор стоял посреди
 * пустоты, подчёркивания не совпадали с цифрами, и было непонятно, сколько
 * символов ждут. Ячейки отвечают на это сами: видно и длину кода, и сколько
 * уже введено.
 *
 * Поведение привычное: цифра переводит курсор дальше, Backspace на пустой
 * ячейке возвращает назад, стрелки ходят по ячейкам, вставка целого кода
 * раскладывается по всем сразу — его обычно копируют из сообщения, а не
 * набирают. Когда заполнена последняя, вызывается `onComplete`: нажимать
 * кнопку после ввода шестой цифры незачем.
 */
import { useRef } from 'react'
import type { ClipboardEvent, KeyboardEvent } from 'react'
import './CodeInput.css'

export interface CodeInputProps {
  value: string
  onChange: (value: string) => void
  /** Код набран целиком. */
  onComplete: (value: string) => void
  length?: number
  disabled?: boolean
  /** Доступное имя группы ячеек. */
  label: string
  /** Неверный код: ячейки подсвечиваются и коротко качаются. */
  invalid?: boolean
  autoFocus?: boolean
}

export function CodeInput({
  value, onChange, onComplete, length = 6, disabled = false, label, invalid = false, autoFocus = false,
}: CodeInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  function focusAt(index: number): void {
    const target = refs.current[Math.max(0, Math.min(length - 1, index))]
    target?.focus()
    target?.select()
  }

  /** Новое значение после правки одной ячейки. */
  function write(index: number, digit: string): void {
    const next = (value.padEnd(length, ' ').slice(0, length).split('')
      .map((char, i) => (i === index ? digit : char))
      .join('')
      .replace(/ /g, ''))
    onChange(next)
    if (digit !== '' && index < length - 1) focusAt(index + 1)
    if (next.length === length) onComplete(next)
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>, index: number): void {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (digits[index]?.trim()) {
        write(index, '')
        return
      }
      /* Ячейка уже пустая — стираем предыдущую и встаём на неё. */
      if (index > 0) {
        write(index - 1, '')
        focusAt(index - 1)
      }
      return
    }
    if (event.key === 'ArrowLeft') { event.preventDefault(); focusAt(index - 1) }
    if (event.key === 'ArrowRight') { event.preventDefault(); focusAt(index + 1) }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (pasted === '') return
    event.preventDefault()
    onChange(pasted)
    focusAt(pasted.length - 1)
    if (pasted.length === length) onComplete(pasted)
  }

  const classes = ['code-input']
  if (invalid) classes.push('code-input--invalid')

  return (
    <div className={classes.join(' ')} role="group" aria-label={label}>
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(node) => { refs.current[index] = node }}
          className="code-input__cell"
          type="text"
          inputMode="numeric"
          /* Подсказка одноразового кода нужна только первой ячейке: иначе
             браузер предлагает подставить код в каждую из шести. */
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          autoFocus={autoFocus && index === 0}
          disabled={disabled}
          maxLength={1}
          value={digit.trim()}
          aria-label={`${label}: ${index + 1}`}
          onChange={(event) => {
            const typed = event.target.value.replace(/\D/g, '')
            if (typed === '') { write(index, ''); return }
            /* В одну ячейку может прилететь несколько цифр — например, при
               автоподстановке кода из сообщения. */
            if (typed.length > 1) {
              const next = (value.slice(0, index) + typed).replace(/\D/g, '').slice(0, length)
              onChange(next)
              focusAt(next.length - 1)
              if (next.length === length) onComplete(next)
              return
            }
            write(index, typed)
          }}
          onKeyDown={(event) => onKeyDown(event, index)}
          onPaste={onPaste}
          onFocus={(event) => event.target.select()}
        />
      ))}
    </div>
  )
}
